import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateMemberRequest,
  CreateMemberResponse,
  DeleteMemberRequest,
  DeleteMemberResponse,
  DownloadMemberExcelRequest,
  DownloadMemberExcelResponse,
  UpdateMemberRequest,
  UpdateMemberResponse,
  ViewAllMembersRequest,
  ViewAllMembersResponse,
  ViewMemberProfileRequest,
  ViewMemberProfileResponse,
  ViewMemberRequest,
  ViewMemberResponse,
  ViewTribeMembersRequest,
  ViewTribeMembersResponse
} from './member.types';

import {
  CREATING_MEMBER,
  DELETING_MEMBER,
  DOWNLOADING_MEMBER_EXCEL,
  RETRIEVING_MEMBER,
  RETRIEVING_MEMBERS,
  RETRIEVING_MEMBER_PROFILE,
  RETRIEVING_TRIBE_MEMBERS,
  UPDATING_MEMBER
} from './member.constants';

import {
  requestCreateMember,
  requestDeleteMember,
  requestDownloadMemberExcel,
  requestUpdateMember,
  requestViewAllMembers,
  requestViewMember,
  requestViewMemberProfile,
  requestViewTribeMembers
} from './member.actions';

import {
  getCreateMemberRequest,
  getDeleteMemberRequest,
  getDownloadMemberExcelRequest,
  getUpdateMemberRequest,
  getViewAllMembersRequest,
  getViewMemberProfileRequest,
  getViewMemberRequest,
  getViewTribeMembersRequest
} from './member.endpoints';

import { setMember, setMembers } from '../../slices/programme/member/member.slice';
import { setProfile } from '../../slices/programme/tribe/tribe.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateMemberRequest({ payload }: SagaPayload<CreateMemberRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_MEMBER));

    const [endpoint, options] = getCreateMemberRequest(requestData);

    const { data }: AxiosResponse<CreateMemberResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(
      requestViewAllMembers({
        requestData: {
          programmeExternalReference: requestData.programmeExternalReference
        }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_MEMBER));
  }
}

export function* watchForCreateMemberRequest() {
  yield takeLatest(requestCreateMember.type, performCreateMemberRequest);
}

export function* performViewAllMembersRequest({ payload }: SagaPayload<ViewAllMembersRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBERS));

    const [endpoint, options] = getViewAllMembersRequest(requestData);

    const { data }: AxiosResponse<ViewAllMembersResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setMembers(data.members));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBERS));
  }
}

export function* watchForViewAllMembersRequest() {
  yield takeLatest(requestViewAllMembers.type, performViewAllMembersRequest);
}

export function* performUpdateMemberRequest({ payload }: SagaPayload<UpdateMemberRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_MEMBER));

    const [endpoint, options] = getUpdateMemberRequest(requestData);

    const { data }: AxiosResponse<UpdateMemberResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(
      requestViewAllMembers({
        requestData: {
          programmeExternalReference: requestData.programmeExternalReference
        }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_MEMBER));
  }
}

export function* watchForUpdateMemberRequest() {
  yield takeLatest(requestUpdateMember.type, performUpdateMemberRequest);
}

export function* performDeleteMemberRequest({ payload }: SagaPayload<DeleteMemberRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_MEMBER));

    const [endpoint, options] = getDeleteMemberRequest(requestData);

    const { data }: AxiosResponse<DeleteMemberResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllMembers({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_MEMBER));
  }
}

export function* watchForDeleteMemberRequest() {
  yield takeLatest(requestDeleteMember.type, performDeleteMemberRequest);
}

export function* performDownloadMemberExcelRequest({
  payload
}: SagaPayload<DownloadMemberExcelRequest, DownloadMemberExcelResponse>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_MEMBER_EXCEL));

    const [endpoint, options] = getDownloadMemberExcelRequest(requestData);

    const { data }: AxiosResponse<DownloadMemberExcelResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_MEMBER_EXCEL));
  }
}

export function* watchForDownloadMemberExcelRequest() {
  yield takeLatest(requestDownloadMemberExcel.type, performDownloadMemberExcelRequest);
}

export function* performViewTribeMembersRequest({ payload }: SagaPayload<ViewTribeMembersRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TRIBE_MEMBERS));

    const [endpoint, options] = getViewTribeMembersRequest(requestData);

    const { data }: AxiosResponse<ViewTribeMembersResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setMembers(data.members));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TRIBE_MEMBERS));
  }
}

export function* watchForViewTribeMembersRequest() {
  yield takeLatest(requestViewTribeMembers.type, performViewTribeMembersRequest);
}

export function* performViewMemberRequest({ payload }: SagaPayload<ViewMemberRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBER));

    const request = getViewMemberRequest(requestData);

    const { data }: AxiosResponse<ViewMemberResponse> = yield call(axios, ...request);

    yield put(setMember(data.members[0]));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBER));
  }
}

export function* watchForViewMemberRequest() {
  yield takeLatest(requestViewMember.type, performViewMemberRequest);
}

export function* performViewMemberProfileRequest({ payload }: SagaPayload<ViewMemberProfileRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBER_PROFILE));

    const request = getViewMemberProfileRequest(requestData);

    const { data }: AxiosResponse<ViewMemberProfileResponse> = yield call(axios, ...request);

    yield put(
      setProfile({
        admin: data.admin,
        username: data.username,
        memberBadgeIconExternalReferences: data.memberBadgeIconExternalReferences,
        profilePicture: data.profilePicture,
        teamNames: data.teamNames
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBER_PROFILE));
  }
}

export function* watchForViewMemberProfileRequest() {
  yield takeLatest(requestViewMemberProfile.type, performViewMemberProfileRequest);
}

export default function* memberSaga() {
  yield all([
    watchForCreateMemberRequest(),
    watchForViewAllMembersRequest(),
    watchForUpdateMemberRequest(),
    watchForDeleteMemberRequest(),
    watchForDownloadMemberExcelRequest(),
    watchForViewTribeMembersRequest(),
    watchForViewMemberRequest(),
    watchForViewMemberProfileRequest()
  ]);
}
